import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Loader, Radio } from '@mantine/core';
import styles from './EmployedsFilterOptionsList.module.scss';
import { ReactComponent as IconClose2 } from '../img/iconClose.svg';
import { EmployedsFilterInput } from './EmployedsFilterInput';
import { EmployedsFilterCheckbox } from '../EmployedsFilterCheckbox';
import ContentLoader from 'react-content-loader';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export interface SelectedItem {
  id: string;
  name: string;
}

export interface FilterOptionsListProps {
  options: FilterOption[];
  selectedValues: string[] | SelectedItem[];
  onChange: (values: string[] | SelectedItem[]) => void;
  showSearch?: boolean;
  searchPlaceholder?: string;
  className?: string;
  singleSelect?: boolean;
  text?: string | null;
  isFetchingTypes?: boolean;
  onSearchValueChange?: (value: string) => void;
  searchValue?: string;
  serverSearch?: boolean;
  hasNextPage?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  storeFullObject?: boolean;
  isLoadingCategorys?: boolean;
}

export const EmployedsFilterOptionsList: FC<FilterOptionsListProps> = ({
  options,
  selectedValues,
  onChange,
  showSearch,
  searchPlaceholder = 'Искать...',
  className,
  singleSelect = false,
  text,
  onSearchValueChange,
  searchValue: controlledSearchValue,
  serverSearch,
  hasNextPage,
  isFetchingMore,
  onLoadMore,
  isLoadingCategorys,
  storeFullObject = false,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const loadMoreLockRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isFetchingMore) {
      loadMoreLockRef.current = false;
    }
  }, [isFetchingMore]);

  const getSelectedIds = (): string[] => {
    if (!storeFullObject) {
      return selectedValues as string[];
    }
    return (selectedValues as SelectedItem[]).map((item) => item.id);
  };

  const getSelectedItems = (): SelectedItem[] => {
    if (!storeFullObject) {
      return (selectedValues as string[]).map((id) => ({ id, name: id }));
    }
    return selectedValues as SelectedItem[];
  };

  const handleLoadMore = useCallback(() => {
    if (!onLoadMore || !hasNextPage || isFetchingMore || loadMoreLockRef.current) {
      return;
    }
    loadMoreLockRef.current = true;
    onLoadMore();
  }, [onLoadMore, hasNextPage, isFetchingMore]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight) return;
    const gapToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = gapToBottom < 72 && el.scrollTop > 12;
    if (nearBottom) {
      handleLoadMore();
    }
  }, [handleLoadMore]);

  const shouldShowSearch = !singleSelect && showSearch !== false;

  const summaryEntries = useMemo(() => {
    const selectedItems = getSelectedItems();

    if (storeFullObject) {
      return selectedItems.map((item) => ({ value: item.id, label: item.name }));
    }

    const byValue = new Map<string, string>();
    options.forEach((o) => byValue.set(String(o.value), o.label));

    return selectedItems
      .map((item) => {
        const label = byValue.get(String(item.id));
        if (!label) return null;
        return { value: item.id, label };
      })
      .filter((e): e is { value: string; label: string } => e !== null);
  }, [options, selectedValues, storeFullObject]);

  const displayedSearchValue = controlledSearchValue ?? searchValue;

  const filteredOptions = useMemo(() => {
    if (serverSearch) return options;
    const valueForFilter = displayedSearchValue.trim().toLowerCase();
    if (!shouldShowSearch || !valueForFilter) return options;

    return options.filter(
      (option) => option.label && option.label.toLowerCase().includes(valueForFilter),
    );
  }, [options, displayedSearchValue, shouldShowSearch, serverSearch]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
  }, [filteredOptions, hasNextPage, isFetchingMore]);

  const scrollableClassName =
    shouldShowSearch || onLoadMore ? styles.scrollableContainer : undefined;

  const handleOptionChange = (option: FilterOption) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentIds = getSelectedIds();
      const currentItems = getSelectedItems();

      if (singleSelect) {
        if (e.target.checked) {
          if (storeFullObject) {
            onChange([{ id: option.value, name: option.label }]);
          } else {
            onChange([option.value]);
          }
        } else {
          onChange([]);
        }
      } else {
        if (e.target.checked) {
          if (storeFullObject) {
            const newItem = { id: option.value, name: option.label };
            onChange([...currentItems, newItem]);
          } else {
            onChange([...currentIds, option.value]);
          }
        } else {
          if (storeFullObject) {
            onChange(currentItems.filter((item) => item.id !== option.value));
          } else {
            onChange(currentIds.filter((id) => id !== option.value));
          }
        }
      }
    };
  };

  const handleRadioChange = (value: string) => {
    if (storeFullObject && value) {
      const option = options.find((o) => o.value === value);
      onChange([{ id: value, name: option?.label || value }]);
    } else {
      onChange(value ? [value] : []);
    }
  };

  const handleDeleteSummary = (value: string) => {
    const currentIds = getSelectedIds();
    const currentItems = getSelectedItems();

    if (storeFullObject) {
      onChange(currentItems.filter((item) => item.id !== value));
    } else {
      onChange(currentIds.filter((id) => id !== value));
    }
  };

  const selectedIds = getSelectedIds();

  return (
    <div className={className}>
      {summaryEntries.length > 0 ? (
        <div className={styles.summaryContainer}>
          {summaryEntries.map((entry) => (
            <div key={entry.value} className={styles.summaryItem}>
              <div style={{ display: 'flex', gap: '4px' }}>
                <span>{entry.label}</span>
                <IconClose2
                  onClick={() => handleDeleteSummary(entry.value)}
                  style={{ width: '16px', height: '16px' }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : text ? (
        <div
          style={{ padding: '4px 12px 16px 10px', fontSize: '16px', color: '#71869D' }}>
          {text}
        </div>
      ) : null}
      {shouldShowSearch && (
        <div className={styles.searchContainer}>
          <EmployedsFilterInput
            type='search'
            value={displayedSearchValue}
            onChange={(val) => {
              const next = String(val);
              if (controlledSearchValue !== undefined) {
                onSearchValueChange?.(next);
              } else {
                setSearchValue(next);
              }
            }}
            placeholder={searchPlaceholder}
            summary={summaryEntries.map((e) => e.label)}
          />
        </div>
      )}

      <div ref={scrollRef} className={scrollableClassName} onScroll={handleScroll}>
        {isLoadingCategorys ? (
          <ContentLoader
            speed={2}
            height={'150px'}
            width='100%'
            className={styles.tab_loader}
            backgroundColor={'#FFFFFF'}
            foregroundColor={'#D8E4FB8C'}>
            <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
          </ContentLoader>
        ) : (
          <div className={styles.options}>
            {filteredOptions.length === 0 && displayedSearchValue && (
              <div className={styles.noOptions}>Ничего не найдено</div>
            )}
            {filteredOptions.length === 0 && !displayedSearchValue && (
              <div className={styles.noOptions}>Нет доступных опций</div>
            )}
            {singleSelect ? (
              <Radio.Group
                value={selectedIds[0] ?? ''}
                onChange={handleRadioChange}
                className={styles.radioGroup}>
                {filteredOptions.map((option) => (
                  <div key={option.value} className={styles.option}>
                    <Radio
                      color='#002CFB'
                      size='sm'
                      value={option.value}
                      label={option.label}
                      iconColor='#002CFB'
                      classNames={{
                        radio: styles.radioButton,
                        icon: styles.radioIcon,
                      }}
                      styles={{
                        label: {
                          fontSize: '16px',
                          lineHeight: '22px',
                          color: '#121922',
                          cursor: 'pointer',
                          maxWidth: '270px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                  </div>
                ))}
              </Radio.Group>
            ) : (
              filteredOptions.map((option) => (
                <div key={option.value} className={styles.option}>
                  <EmployedsFilterCheckbox
                    checked={selectedIds.includes(option.value)}
                    onChange={handleOptionChange(option)}
                    label={option.label}
                  />
                </div>
              ))
            )}
          </div>
        )}

        {isFetchingMore && hasNextPage && (
          <div className={styles.loadMoreRow}>
            <Loader size='xs' color='#002CFB' />
          </div>
        )}
      </div>
    </div>
  );
};
